// packages/web/src/components/MatrixViewer.tsx — mismas props y acciones (onBack / onStart).
// Cambios: encabezado con datos de la sesión en lista definida, "cómo leer" plegado por defecto,
// 4 tarjetas por dimensión con número + color, pesos en barra apilada, CTA fijo abajo.
import { ArrowLeft, ChevronRight, ChevronDown, MessageCircleQuestion } from 'lucide-react';
import { ALL_DIMS, DIM_LABEL, type Dim, type MarzanoMatrix } from '../types';
import { Button, PageHeader } from './ui';

interface MatrixViewerProps {
  matrix: MarzanoMatrix;
  oas?: ReadonlyArray<{ oa_codigo: string; oa_descripcion: string }>;
  oaCodigo: string;
  oaDescripcion: string;
  asignatura: string;
  nivel: string;
  hitoNombre: string;
  studentName: string;
  modality: string;
  onBack: () => void;
  onStart: () => void;
  isStarting: boolean;
}

const DIM_STYLE: Record<Dim, { n: number; text: string; bar: string; soft: string }> = {
  recuperacion: { n: 1, text: 'text-[#2B5C9E]', bar: 'bg-[#2B5C9E]', soft: 'bg-[#E7EFFA]' },
  comprension: { n: 2, text: 'text-[#0B7A72]', bar: 'bg-[#0B7A72]', soft: 'bg-[#E0F4F1]' },
  analisis: { n: 3, text: 'text-[#6A47A6]', bar: 'bg-[#6A47A6]', soft: 'bg-[#EFE9F8]' },
  transferencia: { n: 4, text: 'text-[#A0530B]', bar: 'bg-[#A0530B]', soft: 'bg-[#FBEEDD]' },
};
const CRIT = [
  { k: 'precision', label: 'Precisión', bar: 'bg-marzano-600' },
  { k: 'profundidad', label: 'Profundidad', bar: 'bg-marzano-400' },
  { k: 'independencia', label: 'Independencia', bar: 'bg-emerald-600' },
  { k: 'justificacion', label: 'Justificación', bar: 'bg-amber-500' },
] as const;
const DIFF: Record<string, string> = { low: 'Baja', medium: 'Media', high: 'Alta' };

export function MatrixViewer(props: MatrixViewerProps) {
  const oas = props.oas && props.oas.length > 0 ? props.oas : [{ oa_codigo: props.oaCodigo, oa_descripcion: props.oaDescripcion }];

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-24">
      <PageHeader back={{ label: 'Volver al formulario', onClick: props.onBack }} eyebrow="Matriz Marzano generada" title={props.studentName} />

      <section className="rounded-xl border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-5">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-slate-600">Asignatura</dt><dd className="text-slate-900">{props.asignatura}</dd>
          <dt className="text-slate-600">Nivel</dt><dd className="text-slate-900">{props.nivel}</dd>
          <dt className="text-slate-600">Modalidad</dt><dd className="text-slate-900">{props.modality}</dd>
          <dt className="text-slate-600">Hito</dt><dd className="text-slate-900">{props.hitoNombre}</dd>
        </dl>
        <div>
          <h2 className="text-[13px] font-semibold text-slate-700 mb-1.5">{oas.length > 1 ? `Objetivos de aprendizaje (${oas.length})` : 'Objetivo de aprendizaje'}</h2>
          <ul className="space-y-1.5">
            {oas.map((oa) => (
              <li key={oa.oa_codigo} className="flex items-start gap-2 text-sm">
                <span className="font-mono text-[13px] font-semibold text-marzano-700 bg-marzano-50 rounded px-1.5 py-0.5 shrink-0">{oa.oa_codigo}</span>
                <span className="text-slate-800">{oa.oa_descripcion}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <details className="group rounded-xl border border-slate-200 bg-white">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-3.5">
          <span className="text-sm font-semibold text-slate-900">Cómo leer esta rúbrica</span>
          <ChevronDown aria-hidden className="w-4 h-4 text-slate-500 transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-5 pb-5 space-y-3 text-sm text-slate-700 leading-relaxed">
          <p>El profesor virtual intenta cubrir las <strong>4 dimensiones de Marzano</strong> en orden: recuperar hechos, comprenderlos, analizarlos y transferirlos a situaciones nuevas. Cada respuesta se clasifica en una dimensión y aporta evidencia al reporte.</p>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 px-3.5 py-3"><dt className="font-semibold text-slate-900">Pesos de criterio</dt><dd className="text-[13px] mt-1">Suman 100 % por dimensión: <strong>precisión</strong> (¿es correcto?), <strong>profundidad</strong> (¿va más allá?), <strong>independencia</strong> (¿sin ayudas?), <strong>justificación</strong> (¿fundamenta?).</dd></div>
            <div className="rounded-lg bg-slate-50 px-3.5 py-3"><dt className="font-semibold text-slate-900">Evidencias esperadas</dt><dd className="text-[13px] mt-1">Lo que el motor busca en las respuestas para acreditar la dimensión. No son preguntas.</dd></div>
            <div className="rounded-lg bg-slate-50 px-3.5 py-3"><dt className="font-semibold text-slate-900">Banco de preguntas</dt><dd className="text-[13px] mt-1">Punto de partida validado; el profesor virtual crea variantes según cómo responde el alumno.</dd></div>
            <div className="rounded-lg bg-slate-50 px-3.5 py-3"><dt className="font-semibold text-slate-900">Cierre</dt><dd className="text-[13px] mt-1">Cuando cada dimensión tiene ≥ 3 preguntas y ≥ 4 evidencias, o se agota el tiempo de la modalidad ({props.modality}).</dd></div>
          </dl>
        </div>
      </details>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ALL_DIMS.map((dim) => {
          const d = props.matrix.dimensions[dim];
          const S = DIM_STYLE[dim];
          return (
            <section key={dim} aria-labelledby={`dim-${dim}`} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <header className={`flex items-center gap-2.5 px-4 py-3 ${S.soft}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white ${S.bar}`}>{S.n}</span>
                <h3 id={`dim-${dim}`} className={`font-semibold ${S.text}`}>{DIM_LABEL[dim]}</h3>
                <span className="ml-auto text-xs text-slate-700">{d.evidencias_esperadas.length} evidencias · {d.banco_preguntas.length} preguntas</span>
              </header>
              <div className="p-4 space-y-4">
                <div className="space-y-1.5">
                  <h4 className="text-[13px] font-semibold text-slate-700">Peso de cada criterio</h4>
                  <div className="flex h-2.5 rounded-full overflow-hidden" aria-hidden>
                    {CRIT.map((c) => <span key={c.k} className={c.bar} style={{ width: `${d.criteria_focus[c.k]}%` }} />)}
                  </div>
                  <ul className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                    {CRIT.map((c) => <li key={c.k} className="inline-flex items-center gap-1"><span aria-hidden className={`w-2 h-2 rounded-sm ${c.bar}`} />{c.label} <strong className="tabular-nums">{d.criteria_focus[c.k]}%</strong></li>)}
                  </ul>
                </div>
                <div>
                  <h4 className="text-[13px] font-semibold text-slate-700 mb-1">Evidencias esperadas</h4>
                  <ul className="space-y-1 text-sm text-slate-800 list-disc pl-4 marker:text-slate-400">{d.evidencias_esperadas.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
                <div>
                  <h4 className="text-[13px] font-semibold text-slate-700 mb-1.5">Banco de preguntas</h4>
                  <div className="space-y-1.5">
                    {d.banco_preguntas.map((q) => (
                      <details key={q.id} className="group rounded-lg border border-slate-200 open:border-slate-300">
                        <summary className="cursor-pointer list-none flex items-start gap-2 px-3 py-2 text-sm text-slate-800">
                          <MessageCircleQuestion aria-hidden className="w-4 h-4 text-marzano-500 shrink-0 mt-0.5" />
                          <span className="flex-1">{q.text}</span>
                          <span className="text-xs text-slate-600 shrink-0">{DIFF[q.difficulty] ?? q.difficulty}</span>
                        </summary>
                        <p className="mx-3 mb-2.5 ml-9 border-l-2 border-slate-200 pl-3 text-[13px] text-slate-700"><strong>Evidencia esperada:</strong> {q.expected_evidence}</p>
                      </details>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <div className="fixed bottom-0 inset-x-0 lg:left-64 z-20 border-t border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-8 py-3">
        <div className="max-w-6xl mx-auto flex justify-between items-center gap-3">
          <Button variant="secondary" onClick={props.onBack} leftIcon={<ArrowLeft aria-hidden className="w-4 h-4" />}>Volver al formulario</Button>
          <Button onClick={props.onStart} loading={props.isStarting} rightIcon={<ChevronRight aria-hidden className="w-4 h-4" />}>{props.isStarting ? 'Iniciando…' : 'Iniciar entrevista'}</Button>
        </div>
      </div>
    </div>
  );
}
